import type { FirebaseAuthUserForLastMethod } from '../types/lastAuthMethod.types'

export type { FirebaseAuthUserForLastMethod }

export enum AuthAppId {
  Neo = 'neo',
  Business = 'business',
  Ops = 'ops',
}

export enum LastAuthMethod {
  Email = 'email',
  Google = 'google',
  Apple = 'apple',
  Okta = 'okta',
}

const FIREBASE_SIGN_IN_PROVIDER_TO_METHOD: Record<string, LastAuthMethod> = {
  password: LastAuthMethod.Email,
  'google.com': LastAuthMethod.Google,
  'apple.com': LastAuthMethod.Apple,
}

const SHARED_STORAGE_KEY = 'traba-last-auth-method'

const LEGACY_STORAGE_KEY_PREFIX = 'traba-last-auth-method'

const OPS_COOKIE_SUFFIX = '-ops'

const COOKIE_MAX_AGE_SECONDS = 365 * 24 * 60 * 60

const VALID_METHODS: ReadonlySet<string> = new Set([
  LastAuthMethod.Email,
  LastAuthMethod.Google,
  LastAuthMethod.Apple,
  LastAuthMethod.Okta,
])

function isLastAuthMethod(value: string): value is LastAuthMethod {
  return VALID_METHODS.has(value)
}

function usesSharedNeoBusinessStorage(appId: AuthAppId): boolean {
  return appId === 'neo' || appId === 'business'
}

let testHostnameOverride: string | undefined

export function __setLastAuthMethodHostnameForTests(
  hostname: string | undefined,
): void {
  if (process.env['NODE_ENV'] !== 'test') {
    return
  }
  testHostnameOverride = hostname
}

function getCurrentHostname(): string {
  if (testHostnameOverride !== undefined) {
    return testHostnameOverride
  }
  if (typeof window === 'undefined') {
    return ''
  }
  return window.location.hostname
}

function getTrabaWorkCookieDomain(): string | undefined {
  const hostname = getCurrentHostname()
  if (hostname === 'traba.work' || hostname.endsWith('.traba.work')) {
    return '.traba.work'
  }
  if (hostname === 'traba.ai' || hostname.endsWith('.traba.ai')) {
    return '.traba.ai'
  }
  return undefined
}

function getCookieName(appId: AuthAppId): string {
  if (usesSharedNeoBusinessStorage(appId)) {
    return SHARED_STORAGE_KEY
  }
  return `${SHARED_STORAGE_KEY}${OPS_COOKIE_SUFFIX}`
}

function getLegacyLocalStorageKey(appId: AuthAppId): string {
  return `${LEGACY_STORAGE_KEY_PREFIX}:${appId}`
}

function readCookieValue(name: string): string | null {
  if (typeof document === 'undefined') {
    return null
  }
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : null
}

function writeCookieValue(
  name: string,
  value: string,
  domain: string | undefined,
): void {
  if (typeof document === 'undefined') {
    return
  }
  let cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax`
  if (domain) {
    cookie += `; domain=${domain}`
  }
  document.cookie = cookie
}

function readLocalStorageValue(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeLocalStorageValue(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch {}
}

function removeLocalStorageValue(key: string): void {
  try {
    localStorage.removeItem(key)
  } catch {}
}

function normalizeMethodForApp(
  method: LastAuthMethod,
  appId: AuthAppId,
): LastAuthMethod | null {
  if (appId === 'neo' && method === 'okta') {
    return null
  }
  return method
}

function parseStoredMethod(
  raw: string | null,
  appId: AuthAppId,
): LastAuthMethod | null {
  if (!raw || !isLastAuthMethod(raw)) {
    return null
  }
  return normalizeMethodForApp(raw, appId)
}

function readSharedNeoBusinessMethod(appId: AuthAppId): LastAuthMethod | null {
  const cookieDomain = getTrabaWorkCookieDomain()
  if (cookieDomain) {
    const fromCookie = parseStoredMethod(
      readCookieValue(SHARED_STORAGE_KEY),
      appId,
    )
    if (fromCookie) {
      return fromCookie
    }
  }

  const fromSharedLocal = parseStoredMethod(
    readLocalStorageValue(SHARED_STORAGE_KEY),
    appId,
  )
  if (fromSharedLocal) {
    return fromSharedLocal
  }

  return parseStoredMethod(
    readLocalStorageValue(getLegacyLocalStorageKey(appId)),
    appId,
  )
}

function persistSharedNeoBusinessMethod(method: LastAuthMethod): void {
  const cookieDomain = getTrabaWorkCookieDomain()
  if (cookieDomain) {
    writeCookieValue(SHARED_STORAGE_KEY, method, cookieDomain)
  }

  writeLocalStorageValue(SHARED_STORAGE_KEY, method)
  removeLocalStorageValue(getLegacyLocalStorageKey(AuthAppId.Neo))
  removeLocalStorageValue(getLegacyLocalStorageKey(AuthAppId.Business))
}

export function getLastAuthMethod(appId: AuthAppId): LastAuthMethod | null {
  try {
    if (usesSharedNeoBusinessStorage(appId)) {
      return readSharedNeoBusinessMethod(appId)
    }

    const cookieDomain = getTrabaWorkCookieDomain()
    if (cookieDomain) {
      const fromCookie = parseStoredMethod(
        readCookieValue(getCookieName(appId)),
        appId,
      )
      if (fromCookie) {
        return fromCookie
      }
    }

    return parseStoredMethod(
      readLocalStorageValue(getLegacyLocalStorageKey(appId)),
      appId,
    )
  } catch {}
  return null
}

export function getLastAuthMethodFromFirebaseSignInProvider(
  signInProvider: string,
  appId: AuthAppId,
): LastAuthMethod | null {
  const known = FIREBASE_SIGN_IN_PROVIDER_TO_METHOD[signInProvider]
  if (known) {
    return known
  }
  if (
    appId === AuthAppId.Business &&
    signInProvider.toLowerCase().includes('okta')
  ) {
    return LastAuthMethod.Okta
  }
  return null
}

export async function inferLastAuthMethodFromFirebaseUser(
  user: FirebaseAuthUserForLastMethod,
  appId: AuthAppId,
): Promise<LastAuthMethod | null> {
  try {
    const { signInProvider } = await user.getIdTokenResult()
    if (!signInProvider) {
      return null
    }
    return getLastAuthMethodFromFirebaseSignInProvider(signInProvider, appId)
  } catch {
    return null
  }
}

export function persistLastAuthMethod(
  appId: AuthAppId,
  method: LastAuthMethod,
): void {
  try {
    if (usesSharedNeoBusinessStorage(appId)) {
      persistSharedNeoBusinessMethod(method)
      return
    }

    const cookieDomain = getTrabaWorkCookieDomain()
    const cookieName = getCookieName(appId)
    if (cookieDomain) {
      writeCookieValue(cookieName, method, cookieDomain)
    }

    writeLocalStorageValue(getLegacyLocalStorageKey(appId), method)
  } catch {}
}

export async function persistLastAuthMethodAfterSuccessfulLogin(
  user: FirebaseAuthUserForLastMethod,
  appId: AuthAppId,
): Promise<void> {
  const method = await inferLastAuthMethodFromFirebaseUser(user, appId)
  if (method) {
    persistLastAuthMethod(appId, method)
  }
}
